import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { EzLogo, NavMobileToggle, NavDesktopLinks } from "@/pages/LandingPage";
import { api } from "@/lib/api";
import { ArrowRight, ArrowLeft, MapPin, DollarSign, GraduationCap, Home, Building2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Strip HTML tags for the list-view snippet. Full description is rendered as
// HTML in the detail view (source is ez.health, we control it).
const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
const snippet = (html, len = 220) => {
  const t = stripHtml(html);
  return t.length > len ? t.slice(0, len).trimEnd() + "…" : t;
};

const JobMeta = ({ job }) => {
  const bits = [
    job.remotetype && { icon: Home, text: job.remotetype },
    job.salary && { icon: DollarSign, text: job.salary },
    job.education && { icon: GraduationCap, text: job.education },
    (job.city || job.state || job.country) && {
      icon: MapPin,
      text: [job.city, job.state, job.country].filter(Boolean).join(", "),
    },
    job.company && { icon: Building2, text: job.company },
  ].filter(Boolean);
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-600 font-body">
      {bits.map((b, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          <b.icon className="w-4 h-4 text-ez-blue/70" />
          {b.text}
        </span>
      ))}
    </div>
  );
};

const JobCard = ({ job, onOpen }) => (
  <motion.button
    type="button"
    onClick={onOpen}
    data-testid={`job-card-${job.requisitionid}`}
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -2 }}
    transition={{ duration: 0.3 }}
    className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg hover:border-ez-blue/30 p-6 lg:p-7 transition-all group"
  >
    <div className="flex items-start justify-between gap-4 mb-2">
      <h3 className="text-xl lg:text-2xl font-bold text-ez-teal font-heading group-hover:text-ez-blue transition-colors">
        {job.title}
      </h3>
      <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-ez-blue group-hover:translate-x-1 transition-all flex-shrink-0 mt-1.5" />
    </div>
    {job.category && (
      <p className="text-sm text-slate-500 font-body mb-4">{job.category}</p>
    )}
    <JobMeta job={job} />
    {job.description && (
      <p className="text-sm text-slate-600 font-body leading-relaxed mt-4 line-clamp-3">
        {snippet(job.description)}
      </p>
    )}
  </motion.button>
);

const JobDetail = ({ job, onBack }) => {
  const navigate = useNavigate();
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 lg:p-10"
      data-testid={`job-detail-${job.requisitionid}`}
    >
      <button
        type="button"
        onClick={onBack}
        data-testid="job-detail-back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-ez-blue font-body mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to all jobs
      </button>

      <h1
        className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ez-teal font-heading leading-tight mb-3"
        data-testid="job-detail-title"
      >
        {job.title}
      </h1>
      {job.category && (
        <p className="text-slate-500 font-body mb-6">{job.category}</p>
      )}

      <div className="mb-8 pb-8 border-b border-slate-100">
        <JobMeta job={job} />
      </div>

      {job.description && (
        <div
          className="max-w-none font-body text-slate-700 leading-relaxed [&_p]:mb-4 [&_strong]:text-ez-teal [&_strong]:font-semibold [&_br]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-1 [&_li]:pl-1 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-ez-teal [&_h1]:mt-6 [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-ez-teal [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-ez-teal [&_h3]:mt-5 [&_h3]:mb-2 [&_a]:text-ez-blue [&_a]:underline hover:[&_a]:no-underline"
          dangerouslySetInnerHTML={{ __html: job.description }}
          data-testid="job-detail-description"
        />
      )}

      <div className="mt-10 pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <Button
          data-testid="job-detail-apply-btn"
          onClick={() => navigate(`/apply${window.location.search.replace(/([?&])req=[^&]*&?/, "$1").replace(/[?&]$/, "")}`)}
          className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-10 py-6 text-lg font-semibold shadow-xl shadow-blue-200 hover:-translate-y-0.5 active:translate-y-0 transition-transform"
          size="lg"
        >
          Apply now <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        {job.email && (
          <p className="text-sm text-slate-500 font-body">
            Questions? Email us at{" "}
            <a href={`mailto:${job.email}`} className="text-ez-blue hover:underline font-semibold">
              {job.email}
            </a>
          </p>
        )}
      </div>
    </motion.article>
  );
};

export default function Jobs() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const req = searchParams.get("req");
  const [jobs, setJobs] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Open jobs | Ez Health";
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`${BACKEND_URL}/api/jobs`);
        if (!cancelled) setJobs(res.data.jobs || []);
      } catch (e) {
        if (!cancelled) setError("We couldn't load the job listing right now. Please try again in a moment.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const active = useMemo(
    () => (req && jobs ? jobs.find((j) => j.requisitionid === req) : null),
    [req, jobs]
  );

  const clearReq = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("req");
    setSearchParams(next, { replace: false });
  };

  const openJob = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set("req", id);
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="jobs-page">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 sm:gap-7">
            <NavMobileToggle />
            <a
              href="https://careers.ez.health"
              data-testid="jobs-header-logo-link"
              aria-label="Ez Health careers home"
              className="inline-flex items-center"
            >
              <EzLogo size="h-9" />
            </a>
            <NavDesktopLinks />
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#EDF5FB] to-white" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8 lg:pt-16 lg:pb-10">
            {!active && !req && (
              <>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ez-teal font-heading leading-tight mb-3"
                  data-testid="jobs-list-title"
                >
                  Open roles at Ez Health
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-base md:text-lg text-slate-600 leading-relaxed font-body max-w-2xl"
                >
                  Meaningful work supporting older adults. Fully remote, flexible schedule.
                  Pick a role below to learn more.
                </motion.p>
              </>
            )}
          </div>
        </section>

        <section className="pb-16 lg:pb-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {error && (
              <div
                data-testid="jobs-error"
                className="bg-rose-50 border border-rose-100 text-rose-700 rounded-2xl p-6 font-body"
              >
                {error}
              </div>
            )}

            {!error && jobs === null && !req && (
              <div data-testid="jobs-loading" className="space-y-4">
                {[0, 1].map((i) => (
                  <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-7 animate-pulse">
                    <div className="h-6 bg-slate-100 rounded w-2/3 mb-3" />
                    <div className="h-4 bg-slate-100 rounded w-1/2 mb-5" />
                    <div className="h-3 bg-slate-100 rounded w-full mb-2" />
                    <div className="h-3 bg-slate-100 rounded w-5/6" />
                  </div>
                ))}
              </div>
            )}

            {!error && jobs && jobs.length === 0 && (
              <div
                data-testid="jobs-empty"
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center"
              >
                <h2 className="text-xl font-bold text-ez-teal font-heading mb-2">
                  No open positions right now
                </h2>
                <p className="text-slate-600 font-body">
                  We&apos;ll post new roles here as they open up. In the meantime, feel free to{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/contact-us")}
                    className="text-ez-blue hover:underline font-semibold"
                  >
                    say hello
                  </button>
                  .
                </p>
              </div>
            )}

            {!error && jobs && jobs.length > 0 && active && (
              <JobDetail job={active} onBack={clearReq} />
            )}

            {!error && jobs && jobs.length > 0 && !active && req && (
              // req was passed but doesn't match any current listing
              <div
                data-testid="jobs-not-found"
                className="bg-white rounded-2xl border border-amber-100 shadow-sm p-8 text-center mb-6"
              >
                <h2 className="text-xl font-bold text-ez-teal font-heading mb-2">
                  That role isn&apos;t open right now
                </h2>
                <p className="text-slate-600 font-body mb-4">
                  It may have been filled or closed. Have a look at what&apos;s currently open:
                </p>
                <Button
                  onClick={clearReq}
                  data-testid="jobs-view-all-btn"
                  className="rounded-full bg-ez-blue text-white hover:bg-[#5A9BCF] px-6 py-2.5 font-semibold"
                >
                  See all open roles
                </Button>
              </div>
            )}

            {!error && jobs && jobs.length > 0 && !active && !req && (
              <div className="space-y-4" data-testid="jobs-list">
                {jobs.map((job) => (
                  <JobCard key={job.requisitionid} job={job} onOpen={() => openJob(job.requisitionid)} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
